import { Component, computed, effect, Input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SegmentEvent } from '@app/shared/interfaces';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { Observable } from 'rxjs';
import { VoiceToTextService } from '../../services/ai/voice-to-text.service';

@Component({
  standalone: false,
  selector: 'sbnb-voice-to-text',
  templateUrl: './voice-to-text.component.html',
  styleUrls: ['./voice-to-text.component.scss'],
})
export class VoiceToTextComponent {
  /**
   * The session ID passed from parent component
   */
  @Input() sessionId!: string;

  /**
   * Whether to enable direct transcription feedback (thumbs up/down)
   * Default: false (feedback handled by parent component)
   */
  @Input() enableDirectFeedback = false;

  // Session-aware computed properties - reactive to service signals
  isLoading = computed(() => this.voiceToTextService.isLoading(this.sessionId));
  isRecording = computed(() => this.voiceToTextService.isRecording(this.sessionId));
  isTranscribing = computed(() => this.voiceToTextService.isWorking(this.sessionId));
  isInitializing = computed(() => this.voiceToTextService.isInitializing(this.sessionId));
  isFinalizing = computed(() => this.voiceToTextService.isFinalizing(this.sessionId));
  isEditing = computed(() => this.voiceToTextService.isEditing(this.sessionId));
  wasRecorded = computed(() => this.voiceToTextService.wasRecorded(this.sessionId));
  $finalText = computed(() => this.voiceToTextService.$finalText(this.sessionId));
  $currentTranscription = computed(() => this.voiceToTextService.$currentTranscription(this.sessionId));
  shouldShowButton = computed(() => this.voiceToTextService.shouldShowButton(this.sessionId));

  isMobile = toSignal(this.deviceDetectionService.isMobileViewport());
  enabled$: Observable<boolean>;

  // Feedback state - now using signals for reactivity
  private feedbackGiven = signal(false);
  private feedbackTimer?: ReturnType<typeof setTimeout>;

  // Show feedback when transcription is complete and feedback hasn't been given yet
  // Only show if enableDirectFeedback is true
  showFeedback = computed(() => {
    const shouldShow =
      this.enableDirectFeedback &&
      this.wasRecorded() &&
      this.$finalText() !== '' &&
      !this.feedbackGiven() &&
      !this.isTranscribing() &&
      !this.isInitializing();

    return shouldShow;
  });

  // Audio for recording feedback
  private recordingStartSound: HTMLAudioElement;
  private previousRecordingState = false;

  constructor(
    private voiceToTextService: VoiceToTextService,
    private deviceDetectionService: DeviceDetectionService,
    private segmentIoService: SegmentIoService,
    private posthogService: PosthogService
  ) {
    this.enabled$ = this.voiceToTextService.enabled$;

    // Initialize and preload the recording start sound
    this.recordingStartSound = new Audio('/assets/sounds/record-ready.mp3');
    this.recordingStartSound.preload = 'auto';

    // Load the audio to ensure it's ready to play without delay
    this.recordingStartSound.load();

    // Watch for recording state changes
    effect(() => {
      const isRecording = this.isRecording();

      // Play sound only when transitioning from not recording to recording
      if (isRecording && !this.previousRecordingState) {
        this.playRecordingStartSound();
      }

      // Update previous state for next check
      this.previousRecordingState = isRecording;
    });
  }

  startRecording(): void {
    // Start recording using the session passed from parent
    this.voiceToTextService.startRecording(this.sessionId);
  }

  stopRecording(): void {
    this.voiceToTextService.stopRecording(this.sessionId);
  }

  clear() {
    this.voiceToTextService.clearText(this.sessionId);
    this.resetFeedbackState();
  }

  rateTranscription(rating: 'like' | 'dislike'): void {
    this.feedbackGiven.set(true);

    if (this.feedbackTimer) {
      clearTimeout(this.feedbackTimer);
    }

    const metadata = {
      session_id: this.sessionId,
      transcription_text: this.$finalText(),
    };

    if (rating === 'like') {
      this.segmentIoService.track(SegmentEvent.VoiceToTextLiked, metadata);
    } else {
      this.segmentIoService.track(SegmentEvent.VoiceToTextDisliked, metadata);

      // Trigger survey for dislike feedback after a short delay
      setTimeout(() => {
        // Track to Segment for analytics
        this.segmentIoService.track(SegmentEvent.VoiceToTextSurveyTriggered, metadata);

        // Capture to PostHog for survey triggering
        this.posthogService.capture(SegmentEvent.VoiceToTextSurveyTriggered, metadata);
      }, 500);
    }
  }

  private resetFeedbackState(): void {
    this.feedbackGiven.set(false);

    if (this.feedbackTimer) {
      clearTimeout(this.feedbackTimer);
    }
  }

  private playRecordingStartSound(): void {
    // Reset the audio to the beginning in case it was previously played
    this.recordingStartSound.currentTime = 0;

    // Play the sound, handling any potential errors
    this.recordingStartSound.play().catch((error) => {
      console.warn('Failed to play recording start sound:', error);
    });
  }
}
